import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { ExplorerPane } from 'types/explorer'
import {
  setIsOnboarding,
  skipOnboarding,
} from '@disco/state-management/actions/AppActions'
import { useShortlistUpload } from '@disco/services/upload/hooks'
import type { UploadProvider } from 'types/upload'
import {
  getIsOnboarding,
  getIsOnboardingSkipped,
} from '@disco/state-management/selectors/app'
import {
  getExplorerFirstPane,
  getHasTracks,
  getUploadingToExplorerFilesCount,
} from '@disco/state-management/selectors/explorer'
import { numHiddenShortlistUploads } from '@disco/state-management/selectors/shortlists'
import { UploadProviderLoaders } from 'components/common/upload/upload-provider-loaders'
import Explorer from 'components/explorer'
import OnboardingSplashscreen from 'components/explorer/onboarding-splashscreen'
import { UploadIndicator } from 'components/ui/drop-indicator'
const BLOCKED_VIEWS = ['Browse', 'Starred']

const ExplorerWrapper = (): React.ReactNode => {
  const dispatch = useDispatch()
  const hiddenUploads = useSelector(numHiddenShortlistUploads)
  // Count, not the whole explorer slice: uploads now carry per-file progress,
  // so subscribing to the slice re-rendered this on every progress update.
  const explorerUploads = useSelector(getUploadingToExplorerFilesCount)
  const firstPane: ExplorerPane | null | undefined =
    useSelector(getExplorerFirstPane)
  const hasTracks = useSelector(getHasTracks)
  const isOnboarding = useSelector(getIsOnboarding)
  const isOnboardingSkipped = useSelector(getIsOnboardingSkipped)
  const [numUploaded, setNumUploaded] = useState<number | null>(null)

  const handleUpload = (items: any[]) => {
    setNumUploaded(prev => (prev || 0) + items.length)
  }

  const { uploading, uploadFiles, progress } = useShortlistUpload(handleUpload)
  const isOnBrowseOrStarred = BLOCKED_VIEWS.includes(firstPane?.name)
  const totalUploads = hiddenUploads + explorerUploads

  const handleFilesPicked = (files: any[], provider: UploadProvider) => {
    uploadFiles(files, provider, {
      context: 'splash',
      temporary: true,
      uploadCompletionTask: {
        type: 'show_tracks',
      },
    })
  }

  useEffect(() => {
    if (
      isOnBrowseOrStarred &&
      !hasTracks &&
      !isOnboarding &&
      !isOnboardingSkipped
    ) {
      dispatch(setIsOnboarding(true))
    } else {
      setNumUploaded(null)
    }
  }, [
    dispatch,
    hasTracks,
    isOnBrowseOrStarred,
    isOnboarding,
    isOnboardingSkipped,
  ])

  const handleSkip = useCallback(() => {
    dispatch(skipOnboarding())
  }, [dispatch])
  const handleDismiss = useCallback(() => {
    dispatch(setIsOnboarding(false))
  }, [dispatch])

  return (
    <>
      {!isOnboarding ? (
        <>
          <Explorer />
        </>
      ) : (
        <OnboardingSplashscreen
          onPickedFiles={handleFilesPicked}
          onSkip={handleSkip}
        />
      )}

      {numUploaded === null || uploading ? (
        <UploadIndicator
          tracksNumber={totalUploads}
          progress={progress}
          portal={!isOnboarding}
        />
      ) : isOnboarding ? (
        <UploadIndicator
          tracksNumber={numUploaded !== null ? numUploaded : totalUploads}
          done
          onClick={handleDismiss}
        />
      ) : null}
      <UploadProviderLoaders />
    </>
  )
}

export default ExplorerWrapper
